import React, {useState, useEffect, useContext} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import "./TopicDescriptionCreation.css";
import {Resolution} from "../../../logic/API";
import {TopicDescription} from "../../../logic/APITypes";
import {
  DropDown,
  NumberInput,
  DropDownOption,
  RadioButtonGroup,
} from "@spg/component_library";
import {ScreenSize} from "../../utility/ScreenSize";

/**
 * Controls the storage section when creating, viewing and editing topic descriptions.
 */
function StorageSection(props: {
  alternative: "create" | "view" | "edit";
  waitingForResponse: boolean;
  storageInitTrigger: number;
  storageNullTrigger: number;
  setStorageInfo: (
    unmodifiedInputs: boolean,
    validInputs: boolean,
    maxAgeInS: TopicDescription["data_max_age"],
  ) => void;
  initialTopic: Resolution<TopicDescription> | undefined;
}) {
  const intl = useIntl();
  const screenSizeCategory = useContext(ScreenSize).category;

  const [maxAgeToggle, setMaxAgeToggle] = useState<
    "active" | "inactive" | null
  >(null);
  const [maxAge, setMaxAge] = useState("");
  const maxAgeUnitOptions: DropDownOption<number>[] = [
    {
      value: 60 * 60 * 24,
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.maxAgeUnit.day",
      }),
    },
    {
      value: 60 * 60 * 24 * 7,
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.maxAgeUnit.week",
      }),
    },
    {
      value: 60 * 60 * 24 * 365.25,
      label: intl.formatMessage({
        id: "topicDescriptionCreation.option.maxAgeUnit.year",
      }),
    },
  ];
  const [maxAgeUnit, setMaxAgeUnit] = useState<null | DropDownOption<number>>(
    null,
  );

  const initialTopic = props.initialTopic?.payload;
  const placeholder = props.initialTopic?.unresolved
    ? intl.formatMessage({
        id: "topicDescriptionCreation.inputLabel.placeholder.loading",
      })
    : undefined;

  /** Initializes form with values based on the initialTopic if available,
   * otherwise only info available from the overview table is filled in.
   * For the creation alternatives default values (mostly empty) are used. */
  const setInitialStates = () => {
    if (props.initialTopic?.unresolved) {
      setMaxAgeToggle(null);
    } else if (initialTopic?.data_max_age) {
      setMaxAgeToggle("active");
      const preferredUnit = maxAgeUnitOptions.reduce(
        (prev, curr) =>
          !initialTopic.data_max_age ||
          initialTopic.data_max_age % curr.value === 0
            ? curr
            : prev,
        maxAgeUnitOptions[0],
      );
      setMaxAgeUnit(preferredUnit);
      setMaxAge(
        !initialTopic?.data_max_age
          ? ""
          : "" + Math.floor(initialTopic.data_max_age / preferredUnit.value) ||
              "",
      );
    } else {
      setMaxAgeToggle("inactive");
      setMaxAge("1");
      setMaxAgeUnit(maxAgeUnitOptions[1]); // one week as default
    }
  };

  const maxAgeIsEdited =
    initialTopic &&
    maxAgeToggle === "active" &&
    initialTopic.data_max_age !== null &&
    parseInt(maxAge) * (maxAgeUnit?.value || 0) !== initialTopic.data_max_age;

  const maxAgeHasError =
    maxAgeToggle === "active" &&
    (isNaN(parseInt(maxAge)) ||
      !maxAgeUnit ||
      parseInt(maxAge) * maxAgeUnit.value < 604800 ||
      157788000 < parseInt(maxAge) * maxAgeUnit.value); // between one week and five years in seconds

  const maxAgeMessage =
    isNaN(parseInt(maxAge)) || !maxAgeUnit
      ? "noAge"
      : 604800 > parseInt(maxAge) * maxAgeUnit.value
        ? "tooShort"
        : parseInt(maxAge) * maxAgeUnit.value > 157788000
          ? "tooLong"
          : initialTopic &&
              initialTopic.data_max_age === null &&
              maxAgeToggle === "active"
            ? "activated"
            : initialTopic &&
                maxAgeToggle === "active" &&
                initialTopic.data_max_age &&
                initialTopic.data_max_age > parseInt(maxAge) * maxAgeUnit.value
              ? "decreased"
              : "";

  const unmodifiedInputs =
    (maxAgeToggle === "inactive" && initialTopic?.data_max_age === null) ||
    (maxAgeToggle === "active" &&
      initialTopic?.data_max_age !== null &&
      parseInt(maxAge) * (maxAgeUnit?.value || 0) ===
        initialTopic?.data_max_age);

  useEffect(() => {
    if (props.storageNullTrigger) {
      setMaxAgeToggle("inactive");
      setMaxAge("1");
      setMaxAgeUnit(maxAgeUnitOptions[1]);
    }
  }, [props.storageNullTrigger]);

  useEffect(() => {
    if (props.storageInitTrigger) setInitialStates();
  }, [props.storageInitTrigger]);

  useEffect(() => {
    props.setStorageInfo(
      unmodifiedInputs,
      !maxAgeHasError,
      maxAgeToggle === "inactive" || !maxAgeUnit
        ? null
        : parseInt(maxAge) * maxAgeUnit.value,
    );
  }, [maxAge, maxAgeToggle, maxAgeUnit]);

  useEffect(() => {
    const container = document.querySelector(
      ".storage.topicDescriptionCreationGroupContainer",
    ) as HTMLElement;
    const content = document.querySelector(
      ".storage > .topicDescriptionCreationGroupContent",
    ) as HTMLElement;
    if (!container || !content) return;
    container.style.height =
      (maxAgeToggle == "active" ? content.scrollHeight + 64 : 32) + "px";
  }, [props.alternative, maxAgeToggle, screenSizeCategory]);

  return (
    <>
      <h2>{<FormattedMessage id="topicDescriptionCreation.h2.storage" />}</h2>
      <div className="topicDescriptionCreationMaxAgeToggleContainer">
        <RadioButtonGroup
          label={intl.formatMessage({
            id: "topicDescriptionCreation.inputLabel.maxAgeToggle",
          })}
          idBase={"topicDescriptionCreationMaxAgeToggle"}
          name={"maxAgeToggle"}
          options={{
            active: intl.formatMessage({
              id: "topicDescriptionCreation.inputLabel.maxAgeToggle.active",
            }),
            inactive: intl.formatMessage({
              id: "topicDescriptionCreation.inputLabel.maxAgeToggle.inactive",
            }),
          }}
          value={maxAgeToggle}
          setValue={(v) => {
            setMaxAgeToggle(v as "active" | "inactive" | null);
          }}
          isDisabled={props.waitingForResponse}
          isReadOnly={
            props.alternative === "view" || props.initialTopic?.unresolved
          }
        />
      </div>
      <div className={"topicDescriptionCreationGroupContainer storage active"}>
        <div className="topicDescriptionCreationGroupContent landingPageGrid">
          {maxAgeToggle == "active" && (
            <>
              <div className="topicDescriptionCreationMaxAgeContainer">
                <NumberInput
                  label={intl.formatMessage({
                    id: "topicDescriptionCreation.inputLabel.maxAge",
                  })}
                  placeholder={
                    placeholder ||
                    intl.formatMessage({
                      id: "topicDescriptionCreation.inputLabel.maxAge",
                    })
                  }
                  idBase={"topicDescriptionCreationMaxAge"}
                  value={maxAge}
                  setValue={setMaxAge}
                  minValue={
                    maxAgeUnit
                      ? Math.ceil(604800 / maxAgeUnit.value)
                      : undefined
                  }
                  maxValue={
                    maxAgeUnit
                      ? Math.floor(157788000 / maxAgeUnit.value)
                      : undefined
                  }
                  isError={maxAgeHasError}
                  supportingText=" "
                  isRequired
                  isDisabled={props.waitingForResponse}
                  isReadOnly={
                    props.alternative === "view" ||
                    props.initialTopic?.unresolved
                  }
                  isEdited={maxAgeIsEdited}
                />
              </div>
              <div className="topicDescriptionCreationMaxAgeUnitContainer">
                <DropDown
                  label={intl.formatMessage({
                    id: "topicDescriptionCreation.inputLabel.maxAgeUnit",
                  })}
                  idBase={"topicDescriptionCreationMaxAgeUnit"}
                  options={maxAgeUnitOptions}
                  selectedOption={maxAgeUnit}
                  setSelectedOption={setMaxAgeUnit}
                  isError={maxAgeHasError}
                  isRequired
                  isDisabled={props.waitingForResponse}
                  isReadOnly={
                    props.alternative === "view" ||
                    props.initialTopic?.unresolved
                  }
                  isEdited={maxAgeIsEdited}
                />
              </div>
              {props.alternative !== "view" && (
                <p
                  className={`topicDescriptionCreationMaxAgeMessage${
                    maxAgeMessage ? " error" : ""
                  }`}
                >
                  {
                    <FormattedMessage
                      id={`topicDescriptionCreation.p.maxAgeMessage.${
                        maxAgeMessage || "default"
                      }`}
                      values={{b: (chunks) => <b>{chunks}</b>}}
                    />
                  }
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

export {StorageSection};
